import * as React from 'react'
import { useState } from 'react'
import { Box, Paper, Typography, TextField, IconButton, CircularProgress } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import dayjs from 'dayjs'
import { updateSaleDiRiferimento } from '../../services/updateSaleDiRiferimento'
import { isValidUser } from '../../OnlyWithFunctions'
import { Roles } from '../../enums/roles'

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-')

const labelSx = {
  color: 'text.secondary',
  display: 'block',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
}

const valueSx = {
  fontSize: '0.95rem',
  fontWeight: 500,
  color: 'text.primary',
  mt: 0.5,
}

const EsitoValue = ({ value }) => {
  if (!value) return <Typography sx={valueSx}>-</Typography>
  const isPresente = value === 'PRESENTE'
  const dotColor = isPresente ? '#d32f2f' : '#2e7d32' // rosso se presente, verde se non presente

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 500 }}>{value}</Typography>
    </Box>
  )
}

const SaleDiRiferimentoValue = ({ idSoggettoTerzo, value }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [saleDiRiferimento, setSaleDiRiferimento] = useState(value || '')
  const [saving, setSaving] = useState(false)
  const canEdit = isValidUser(Roles.AML_DESK.role)

  const handleSalva = async () => {
    setSaving(true)
    try {
      await updateSaleDiRiferimento(idSoggettoTerzo, saleDiRiferimento)
      setIsEditing(false)
    } catch (error) {
      console.error('Errore durante il salvataggio della sale di riferimento:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 0.5 }}>
      {isEditing ? (
        <TextField
          size="small"
          variant="standard"
          value={saleDiRiferimento}
          onChange={(e) => setSaleDiRiferimento(e.target.value)}
          sx={{ width: '90%' }}
        />
      ) : (
        <Typography sx={valueSx}>{saleDiRiferimento || '-'}</Typography>
      )}
      {!canEdit ? null : isEditing ? (
        <IconButton size="small" onClick={handleSalva} disabled={saving} aria-label="Salva sale di riferimento">
          {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
        </IconButton>
      ) : (
        <IconButton size="small" onClick={() => setIsEditing(true)} aria-label="Modifica sale di riferimento">
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  )
}

const campi = [
  { field: 'cognome', label: 'Cognome' },
  { field: 'nome', label: 'Nome' },
  { field: 'codiceFiscale', label: 'Codice Fiscale' },
  { field: 'dataScreening', label: 'Data screening', format: formatDate },
  { field: 'esito', label: 'Esito', render: (value) => <EsitoValue value={value} /> },
  { field: 'ndgClienteCollegato', label: 'NDG Cliente collegato' },
  { field: 'saleDiRiferimento', label: 'Sale di riferimento' },
  { field: 'pivaClienteCollegato', label: 'PIVA Cliente collegato' },
  { field: 'notizieDaVerificare', label: 'N. notizie nuove' },
]

/**
 * Riepilogo con le informazioni anagrafiche/di screening del soggetto terzo,
 * mostrato sopra la lista degli accordion di monitoraggio.
 *
 * Props:
 * - soggettoTerzo: dati del soggetto terzo (selectedRowData)
 */
const RiepilogoSoggettoTerzo = ({ soggettoTerzo = {} }) => {
  return (
    <Paper elevation={0} sx={{ p: 2, backgroundColor: 'transparent' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          rowGap: 3,
          columnGap: 4,
        }}
      >
        {campi.map((campo) => {
          const valore = soggettoTerzo?.[campo.field]
          return (
            <Box key={campo.field}>
              <Typography sx={labelSx}>{campo.label}</Typography>
              {campo.field === 'saleDiRiferimento' ? (
                <SaleDiRiferimentoValue idSoggettoTerzo={soggettoTerzo?.id} value={valore} />
              ) : campo.render ? (
                campo.render(valore)
              ) : (
                <Typography sx={valueSx}>{campo.format ? campo.format(valore) : valore || '-'}</Typography>
              )}
            </Box>
          )
        })}
      </Box>
    </Paper>
  )
}

export default RiepilogoSoggettoTerzo
